import React, { useContext } from 'react';

import styled from 'styled-components';

import { WIDGET_TYPES } from 'common/Widgets/constants';

import { CommunityViewWidgetModalContext } from './CommunityViewWidgetModalContext';
import { createFilter, deleteFilter, updatePartialFilter } from '../../../apis/FilterAPI';
import { createWidget, updatePartialWidget } from '../../../apis/WidgetAPI';
import { WIDGET_MODAL_TYPE } from '../../../common/Widgets/Widget/constants';
import { useRootContext } from '../../layout/RootContext';
import Button from '../../lib/Button';
import { useCommunityViewContext } from '../CommunityViewContext';

const Container = styled.div`
  display: flex;
  justify-content: end;
  gap: 10px;
  padding: 20px 30px;
  border-top: 1px solid var(--line-gray);
`;

const CommunityViewWidgetModalFooter = () => {
  const { showMessage } = useRootContext();
  const { viewId, viewRefetch } = useCommunityViewContext();
  const {
    id,
    type,
    name,
    note,
    filterId,
    competitors,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    careLevelsStatsType,
    medicationLevelsStatsType,
    reviewSources,
    reviewCategories,
    swotCategories,
    amenityCategories,
    contractTypes,
    communityContractType,
    communityContractSelectionType,
    priceDisplay,
    displayValue,
    modalType,
    initialRange,
    initialFormat,
    initialOrientation,
    groupBy,
    onClose,
  } = useContext(CommunityViewWidgetModalContext);

  const handleCreateWidget = async (filterId: number) => {
    await createWidget({
      type,
      name,
      note,
      view: viewId,
      filter: filterId,
      group_by: groupBy,
      initial_settings: {
        range: initialRange,
        format: initialFormat,
        orientation: initialOrientation,
        display_value: displayValue,
        price_display: priceDisplay,
      },
    });
  };

  const handleUpdateWidget = async (widgetId: number) => {
    await updatePartialWidget(widgetId, {
      name,
      note,
      group_by: groupBy,
      initial_settings: {
        range: initialRange,
        format: initialFormat,
        orientation: initialOrientation,
        display_value: displayValue,
        price_display: priceDisplay,
      },
    });
  };

  const handleUpdateFilter = async (filterId: number) => {
    await updatePartialFilter(filterId, {
      competitors,
      care_types: careTypes,
      room_types: roomTypes,
      room_types_price_display: roomTypesPriceDisplay,
      care_levels: careLevels,
      medication_levels: medicationLevels,
      review_sources: reviewSources,
      review_categories: reviewCategories,
      swot_categories: swotCategories,
      care_levels_stats_type: careLevelsStatsType,
      medication_levels_stats_type: medicationLevelsStatsType,
      amenity_categories: amenityCategories,
      contract_types: contractTypes,
      community_contract_type: communityContractType,
      community_contract_selection_type: communityContractSelectionType,
    });
  };

  const handleCreate = async () => {
    await createFilter({
      competitors,
      care_types: careTypes,
      room_types: roomTypes,
      room_types_price_display: roomTypesPriceDisplay,
      care_levels: careLevels,
      medication_levels: medicationLevels,
      review_sources: reviewSources,
      review_categories: reviewCategories,
      swot_categories: swotCategories,
      amenity_categories: amenityCategories,
      contract_types: contractTypes,
      community_contract_type: communityContractType,
      community_contract_selection_type: communityContractSelectionType,
      care_levels_stats_type: careLevelsStatsType,
      medication_levels_stats_type: medicationLevelsStatsType,
    })
      .then((response) => {
        handleCreateWidget(response?.id)
          .then((response) => {
            showMessage('success', 'New widget added to the view.');
            viewRefetch();
            onClose();
          })
          .catch((reason) => {
            deleteFilter(response?.id);
            showMessage('error', 'Trouble creating new widget.');
          });
      })
      .catch((reason: any) => {
        showMessage('error', 'Trouble creating new widget.');
      });
  };

  const handleUpdate = async () => {
    if (id) {
      handleUpdateWidget(id)
        .then((response) => {
          if (filterId) {
            handleUpdateFilter(filterId)
              .then((response) => {
                showMessage('success', 'Widget successfully updated.');
                viewRefetch();
                onClose();
              })
              .catch((reason: any) => {
                showMessage('error', 'Trouble updating widget filter.');
              });
          }
        })
        .catch((reason: any) => {
          showMessage('error', 'Trouble updating widget.');
        });
    }
  };

  const disableSaveAction = Boolean(type === WIDGET_TYPES.AMENITIES && !amenityCategories.length);

  return (
    <Container>
      {modalType === WIDGET_MODAL_TYPE.ADD && (
        <Button disabled={disableSaveAction} type={'primary'} onClick={handleCreate} style={{ width: 140 }}>
          Add Widget
        </Button>
      )}

      {modalType === WIDGET_MODAL_TYPE.EDIT && (
        <Button disabled={!id || disableSaveAction} type={'primary'} onClick={handleUpdate} style={{ width: 140 }}>
          Edit Widget
        </Button>
      )}
      <Button onClick={onClose} style={{ width: 120 }}>
        Cancel
      </Button>
    </Container>
  );
};

export default CommunityViewWidgetModalFooter;
